// Dad's whole life as one ordered, mappable journey — drives the
// "Play His Life" cinematic on the World Atlas.

import { VOYAGES } from "./voyages";
import { CRUISES } from "./journey";

const voyage = (slug) => VOYAGES.find((v) => v.slug === slug);

// Expand a ship's port list into journey events (skipping the first port,
// which is covered by the ship's own "major" opening event).
const shipPorts = (slug, chapter, year, title) =>
  voyage(slug)
    .ports.slice(1)
    .map((p) => ({
      year,
      chapter,
      title,
      place: p.name,
      sub: p.note || p.country || "",
      lat: p.lat,
      lng: p.lng,
      major: !!p.note,
    }));

const cruiseLegs = CRUISES.flatMap((c) => {
  const [home, ...rest] = c.ports;
  const events = [
    {
      year: String(c.year),
      chapter: "III",
      title: c.ship,
      place: home.name,
      sub: c.title,
      lat: home.lat,
      lng: home.lng,
      major: true,
    },
  ];
  if (!c.portsUnknown) {
    for (const p of rest) {
      events.push({
        year: String(c.year),
        chapter: "III",
        title: c.ship,
        place: p.name,
        sub: p.country || "",
        lat: p.lat,
        lng: p.lng,
        major: false,
      });
    }
  }
  return events;
});

export const LIFE_JOURNEY = [
  // ------------------------------- Chapter I — The Royal Navy
  {
    year: "1962",
    chapter: "I",
    title: "HMS Ganges",
    place: "Shotley Gate, Suffolk",
    sub: "A sixteen-year-old boy entrant joins the Royal Navy",
    lat: 51.9577,
    lng: 1.2662,
    major: true,
  },
  {
    year: "1963",
    chapter: "I",
    title: "HMS Tenby",
    place: "Portland",
    sub: "First sea draft — Home Fleet training squadron",
    lat: 50.5719,
    lng: -2.438,
    major: true,
  },
  {
    year: "1963–65",
    chapter: "I",
    title: "HMS Centaur",
    place: "Portsmouth",
    sub: "Light fleet carrier — East of Suez",
    lat: 50.8198,
    lng: -1.088,
    major: true,
  },
  ...shipPorts("hms-centaur", "I", "1963–65", "HMS Centaur"),
  {
    year: "1965–67",
    chapter: "I",
    title: "HMS Pellew",
    place: "Portland",
    sub: "Anti-submarine frigate — Home & Mediterranean Fleets",
    lat: 50.5719,
    lng: -2.438,
    major: true,
  },
  {
    year: "1967",
    chapter: "I",
    title: "HMS Jaguar",
    place: "Persian Gulf",
    sub: "Anti-aircraft frigate — Gulf & Indian Ocean patrols",
    lat: 26.5,
    lng: 52.0,
    major: true,
  },
  {
    year: "1967–69",
    chapter: "I",
    title: "HMS Hampshire",
    place: "Portsmouth",
    sub: "Guided-missile destroyer — around South America",
    lat: 50.8198,
    lng: -1.088,
    major: true,
  },
  ...shipPorts("hms-hampshire", "I", "1967–69", "HMS Hampshire"),
  {
    year: "1969–72",
    chapter: "I",
    title: "HMS Sultan",
    place: "Gosport, Hampshire",
    sub: "Mechanician's course — RN School of Marine Engineering",
    lat: 50.7963,
    lng: -1.1267,
    major: true,
  },
  {
    year: "1972–74",
    chapter: "I",
    title: "HMS Bulwark",
    place: "Malta",
    sub: "Commando carrier — Mediterranean, Caribbean, Baltic",
    lat: 35.8989,
    lng: 14.5146,
    major: true,
  },
  ...shipPorts("hms-bulwark", "I", "1972–74", "HMS Bulwark"),
  {
    year: "1974–76",
    chapter: "I",
    title: "HMS Dolphin",
    place: "Fort Blockhouse, Gosport",
    sub: "The Submarine School",
    lat: 50.7902,
    lng: -1.1187,
    major: true,
  },
  {
    year: "1976–77",
    chapter: "I",
    title: "HMS Renown",
    place: "Faslane, Scotland",
    sub: "Polaris deterrent patrol — CPO Mechanician First Class",
    lat: 56.0679,
    lng: -4.8181,
    major: true,
  },
  {
    year: "1977–78",
    chapter: "I",
    title: "HMS Sultan",
    place: "Gosport",
    sub: "Final draft — released into the real world, March 1978",
    lat: 50.7963,
    lng: -1.1267,
    major: true,
  },
  // ------------------------------- Chapter II — A Career in Information
  {
    year: "1978",
    chapter: "II",
    title: "Mark One Publications",
    place: "Weybridge, Surrey",
    sub: "Technical Author — out of the Navy, into the written word",
    lat: 51.3715,
    lng: -0.4577,
    major: true,
  },
  {
    year: "1984",
    chapter: "II",
    title: "Offshore Design Ltd",
    place: "Aberdeen, Scotland",
    sub: "General Manager — Technical Publications",
    lat: 57.1497,
    lng: -2.0943,
    major: true,
  },
  {
    year: "1991",
    chapter: "II",
    title: "Free Burgess of Aberdeen",
    place: "Aberdeen",
    sub: "Admitted a Free Burgess and Guild Member — Bon Accord",
    lat: 57.1497,
    lng: -2.0943,
    major: true,
  },
  {
    year: "1998–99",
    chapter: "II",
    title: "Elf — Elgin Franklin",
    place: "North Sea",
    sub: "Engineering data warehouse, offshore",
    lat: 57.0,
    lng: 1.75,
    major: true,
  },
  {
    year: "2000–01",
    chapter: "II",
    title: "ARCO / BP — Boqueron",
    place: "Maturín, Venezuela",
    sub: "EDMS specification & operations handover",
    lat: 9.7457,
    lng: -63.1834,
    major: true,
  },
  {
    year: "2001–04",
    chapter: "II",
    title: "BP Gulf of Mexico Deepwater",
    place: "Houston, Texas",
    sub: "Mad Dog, Holstein, Thunder Horse & Atlantis",
    lat: 29.7604,
    lng: -95.3698,
    major: true,
  },
  {
    year: "2004–06",
    chapter: "II",
    title: "BP Greater Plutonio",
    place: "Sunbury, England",
    sub: "VNET Engineering Portal — project phase",
    lat: 51.4045,
    lng: -0.4177,
    major: true,
  },
  {
    year: "2006–10",
    chapter: "II",
    title: "BP Plutonio FPSO",
    place: "Luanda, Angola",
    sub: "Commissioning, first oil, and into full production",
    lat: -8.839,
    lng: 13.2894,
    major: true,
  },
  {
    year: "2011–13",
    chapter: "II",
    title: "BP Safety & Operational Risk",
    place: "Sunbury",
    sub: "Global IM lead — with quarterly trips back to Luanda",
    lat: 51.4045,
    lng: -0.4177,
    major: true,
  },
  {
    year: "2014",
    chapter: "II",
    title: "Retirement",
    place: "Aberdeen",
    sub: "Forty-eight working years. Time for golf and grandchildren.",
    lat: 57.1497,
    lng: -2.0943,
    major: true,
  },
  // ------------------------------- Chapter III — The World
  ...cruiseLegs,
];
